import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { autovoteApi } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import TagInput from './TagInput/TagInput';
import hiveService from '../services/hive';

function AutovotingAdvanced() {
  const { user } = useAuth();
  const { voteDefaultWeight, autovoteAdvancedGlobalPause, autovoteAdvancedManaLimit, saveSettings } = useSettings();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Global Settings State
  const [globalPause, setGlobalPause] = useState(autovoteAdvancedGlobalPause);
  const [manaLimit, setManaLimit] = useState(autovoteAdvancedManaLimit);
  const [initialManaLimit, setInitialManaLimit] = useState(autovoteAdvancedManaLimit);
  const [savingManaLimit, setSavingManaLimit] = useState(false);

  useEffect(() => {
    setGlobalPause(autovoteAdvancedGlobalPause);
  }, [autovoteAdvancedGlobalPause]);

  useEffect(() => {
    setManaLimit(autovoteAdvancedManaLimit);
    setInitialManaLimit(autovoteAdvancedManaLimit);
  }, [autovoteAdvancedManaLimit]);

  const handleToggleGlobalPause = async () => {
    const newState = !globalPause;
    setGlobalPause(newState); // optimistic
    const success = await saveSettings({ autovote_advanced_global_pause: newState.toString() });
    if (!success) {
      setGlobalPause(!newState);
      alert('Failed to update advanced global pause setting.');
    }
  };

  const handleSaveManaLimit = async () => {
    let finalLimit = manaLimit === '' ? 0 : manaLimit;
    setManaLimit(finalLimit);
    setSavingManaLimit(true);
    const success = await saveSettings({ autovote_advanced_mana_limit: finalLimit.toString() });
    if (success) {
      setInitialManaLimit(finalLimit);
    } else {
      alert('Failed to update advanced minimum mana limit.');
    }
    setSavingManaLimit(false);
  };

  // Form State
  const [targetUsers, setTargetUsers] = useState([]);
  const [targetCommunities, setTargetCommunities] = useState([]);
  const [targetTags, setTargetTags] = useState([]);
  const [tagsMatchAll, setTagsMatchAll] = useState(true);
  const [minWordCount, setMinWordCount] = useState('');
  
  const [articleWaitTime, setArticleWaitTime] = useState(0);
  const [articleWeight, setArticleWeight] = useState(voteDefaultWeight);
  const [articleDailyLimit, setArticleDailyLimit] = useState('');
  const [isScaleWeight, setIsScaleWeight] = useState(false);
  
  const [savingRule, setSavingRule] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ addRule: false });

  // User Search State
  const [userSearchValue, setUserSearchValue] = useState('');
  const [userSearching, setUserSearching] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);

  useEffect(() => {
    const q = userSearchValue.trim().toLowerCase();
    if (q.length < 3) {
      setUserSearchResults([]);
      setUserSearching(false);
      return;
    }

    let cancelled = false;
    setUserSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const matches = await hiveService.searchAccounts(q, 10);
        if (cancelled) return;
        setUserSearchResults(matches.filter((name) => !targetUsers.includes(name.toLowerCase())));
      } catch (_err) {
        if (cancelled) return;
        setUserSearchResults([]);
      } finally {
        if (!cancelled) {
          setUserSearching(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [userSearchValue, targetUsers]);

  const handleAddUser = (username) => {
    if (!targetUsers.includes(username)) {
      setTargetUsers([...targetUsers, username]);
    }
    setUserSearchValue('');
    setUserSearchResults([]);
  };

  const handleRemoveUser = (username) => {
    setTargetUsers(targetUsers.filter(u => u !== username));
  };

  // Community Search State
  const [commSearchValue, setCommSearchValue] = useState('');
  const [commSearching, setCommSearching] = useState(false);
  const [commSearchResults, setCommSearchResults] = useState([]);

  useEffect(() => {
    const q = commSearchValue.trim().toLowerCase();
    if (q.length < 2) {
      setCommSearchResults([]);
      setCommSearching(false);
      return;
    }

    let cancelled = false;
    setCommSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const res = await api.get(`/feed/communities?search=${encodeURIComponent(q)}&limit=10`);
        if (cancelled) return;
        if (res.data && res.data.communities) {
          setCommSearchResults(res.data.communities.filter((c) => !targetCommunities.includes(c.name)));
        } else {
          setCommSearchResults([]);
        }
      } catch (_err) {
        if (cancelled) return;
        setCommSearchResults([]);
      } finally {
        if (!cancelled) {
          setCommSearching(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [commSearchValue, targetCommunities]);

  const handleAddCommunity = (commName) => {
    if (!targetCommunities.includes(commName)) {
      setTargetCommunities([...targetCommunities, commName]);
    }
    setCommSearchValue('');
    setCommSearchResults([]);
  };

  const handleRemoveCommunity = (commName) => {
    setTargetCommunities(targetCommunities.filter(c => c !== commName));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await autovoteApi.getRules(user.username);
      setRules((res.rules || []).filter(r => r.is_advanced));
    } catch (err) {
      console.error("Failed to load advanced autovote rules:", err);
    } finally {
      setLoading(false);
    }
  }, [user.username]);

  useEffect(() => {
    if (user && user.username) {
      loadRules();
    }
  }, [user, loadRules]);

  const handleResetForm = () => {
    setTargetUsers([]);
    setTargetCommunities([]);
    setTargetTags([]);
    setTagsMatchAll(true);
    setMinWordCount('');
    setArticleWaitTime(0);
    setArticleWeight(voteDefaultWeight);
    setArticleDailyLimit('');
    setIsScaleWeight(false);
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (targetUsers.length === 0 && targetCommunities.length === 0 && targetTags.length === 0 && !minWordCount) {
      alert("Please provide at least one condition (User, Community, Tags, or Word Count).");
      return;
    }

    setSavingRule(true);
    try {
      const payload = {
        is_advanced: true,
        target_users: targetUsers.length > 0 ? targetUsers : null,
        target_communities: targetCommunities.length > 0 ? targetCommunities : null,
        target_tags: targetTags.length > 0 ? targetTags : null,
        tags_match_all: tagsMatchAll,
        min_word_count: minWordCount ? parseInt(minWordCount, 10) : null,
        
        vote_articles: true,
        article_wait_time: parseInt(articleWaitTime, 10) || 0,
        article_weight: parseInt(articleWeight, 10) || 100,
        article_daily_limit: parseInt(articleDailyLimit, 10) || null,
        is_scale_weight: isScaleWeight,
        
        vote_short_content: false,
        vote_comments: false,
      };

      await autovoteApi.saveRule(user.username, payload);
      await loadRules();
      handleResetForm();
    } catch (err) {
      alert("Failed to save advanced rule: " + (err.response?.data?.error || err.message));
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this advanced rule?")) return;
    try {
      await autovoteApi.deleteRule(user.username, ruleId);
      await loadRules();
    } catch (err) {
      alert("Failed to delete rule.");
    }
  };

  const handleToggleRulePause = async (rule) => {
    try {
      const newPausedState = !rule.is_paused;
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_paused: newPausedState } : r));
      await autovoteApi.toggleRulePause(user.username, rule.id, newPausedState);
    } catch (err) {
      await loadRules();
      alert("Failed to toggle pause state.");
    }
  };

  return (
    <div className="autovoting-following-container">
      {/* GLOBAL ADVANCED SETTINGS */}
      <div className="av-section" style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '16px', marginBottom: '24px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Advanced Autovoting Setting</h2>
            <p style={{ margin: '5px 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {!globalPause ? 'ACTIVE' : 'DISABLED'}
            </p>
          </div>
          <button
            type="button"
            className={`av-pause-toggle ${globalPause ? 'on' : 'off'}`}
            onClick={handleToggleGlobalPause}
            aria-label="Toggle advanced global pause"
          >
            <span className={`av-pause-option ${!globalPause ? 'active' : ''}`}>Enable</span>
            <span className={`av-pause-option ${globalPause ? 'active' : ''}`}>Disable</span>
            <span className="av-pause-slider"></span>
          </button>
        </div>
        
        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Advanced Rule Minimum Voting Mana</h3>
            <p style={{ margin: '5px 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Skip advanced rule autovoting if your mana drops below this percentage.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <input
              type="range"
              min="0"
              max="100"
              value={manaLimit === '' ? 0 : manaLimit}
              onChange={(e) => setManaLimit(parseInt(e.target.value, 10))}
              style={{ width: '200px' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="number"
                min="0"
                max="100"
                value={manaLimit}
                onChange={(e) => {
                  let valStr = e.target.value;
                  if (valStr === '') {
                     setManaLimit('');
                  } else {
                     let val = parseInt(valStr, 10);
                     if (val > 100) val = 100;
                     if (val < 0) val = 0;
                     setManaLimit(val);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if ((manaLimit === '' ? 0 : manaLimit) !== initialManaLimit) {
                      handleSaveManaLimit();
                    }
                  }
                }}
                className="av-form-control"
                style={{ width: '60px', textAlign: 'center', padding: '4px' }}
              />
              <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>%</span>
            </div>
            {(manaLimit === '' ? 0 : manaLimit) !== initialManaLimit && (
              <button 
                type="button" 
                className="av-btn av-btn-primary" 
                onClick={handleSaveManaLimit}
                disabled={savingManaLimit}
                style={{ padding: '6px 12px', fontSize: '0.9rem' }}
              >
                {savingManaLimit ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ADD ADVANCED RULE SECTION */}
      <div className="av-section">
        <div 
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => toggleSection('addRule')}
        >
          <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Add Advanced Rule</h2>
          <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>{expandedSections.addRule ? '▲' : '▼'}</span>
        </div>
        
        {expandedSections.addRule && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
            <form onSubmit={handleSaveRule}>
              {/* Users */}
              <div className="av-form-group">
                <label>Target Users (Optional)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="av-form-control"
                    type="text"
                    value={userSearchValue}
                    onChange={(e) => setUserSearchValue(e.target.value)}
                    placeholder="Search Hive accounts to add (min 3 chars)..."
                    autoComplete="off"
                  />
                  {userSearching && <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Searching...</div>}
                  {!userSearching && userSearchValue.trim().length >= 3 && userSearchResults.length === 0 && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>No matching accounts</div>
                  )}
                  {!userSearching && userSearchResults.length > 0 && (
                    <ul className="list-member-search-results" style={{ position: 'absolute', zIndex: 10, width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', padding: 0, margin: '4px 0 0', listStyle: 'none', maxHeight: '200px', overflowY: 'auto' }}>
                      {userSearchResults.map((account) => {
                        const normalized = String(account).toLowerCase();
                        return (
                          <li key={normalized} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <img
                                src={`https://images.hive.blog/u/${normalized}/avatar/small`}
                                alt={normalized}
                                style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                                onError={e => { e.target.src = '/default-avatar.png'; }}
                              />
                              <span style={{ fontWeight: 500 }}>@{normalized}</span>
                            </div>
                            <button
                              type="button"
                              className="av-btn av-btn-primary"
                              style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                              onClick={() => handleAddUser(normalized)}
                            >
                              Add
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                
                {targetUsers.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {targetUsers.map(u => (
                      <div key={u} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: 'var(--bg-secondary)', borderRadius: '4px', border: '1px solid var(--border-color)', fontSize: '0.9rem' }}>
                        @{u}
                        <button type="button" onClick={() => handleRemoveUser(u)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, fontSize: '1rem', lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>If present, article must be authored by one of these users.</div>
              </div>

              {/* Communities */}
              <div className="av-form-group">
                <label>Communities (Optional)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="av-form-control"
                    type="text"
                    value={commSearchValue}
                    onChange={(e) => setCommSearchValue(e.target.value)}
                    placeholder="Search communities by name or title..."
                    autoComplete="off"
                  />
                  {commSearching && <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Searching...</div>}
                  {!commSearching && commSearchValue.trim().length >= 2 && commSearchResults.length === 0 && (
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>No matching communities</div>
                  )}
                  {!commSearching && commSearchResults.length > 0 && (
                    <ul className="list-member-search-results" style={{ position: 'absolute', zIndex: 10, width: '100%', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', padding: 0, margin: '4px 0 0', listStyle: 'none', maxHeight: '200px', overflowY: 'auto' }}>
                      {commSearchResults.map((comm) => {
                        return (
                          <li key={comm.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 500 }}>{comm.title}</span>
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>hive-{comm.name}</span>
                            </div>
                            <button
                              type="button"
                              className="av-btn av-btn-primary"
                              style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                              onClick={() => handleAddCommunity(comm.name)}
                            >
                              Add
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                
                {targetCommunities.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {targetCommunities.map(c => (
                      <div key={c} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: 'var(--bg-secondary)', borderRadius: '4px', border: '1px solid var(--border-color)', fontSize: '0.9rem' }}>
                        hive-{c}
                        <button type="button" onClick={() => handleRemoveCommunity(c)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0, fontSize: '1rem', lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Article must be posted in one of these communities.</div>
              </div>

              {/* Tags */}
              <div className="av-form-group">
                <label>Tags (Optional)</label>
                <TagInput tags={targetTags} onChange={setTargetTags} placeholder="e.g. photography, travel" />
                
                {targetTags.length > 1 && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center' }}>
                    <label style={{ margin: 0, fontWeight: 500 }}>Tag Matching:</label>
                    <button type="button" className={`av-type-btn ${tagsMatchAll ? 'active' : ''}`} onClick={() => setTagsMatchAll(true)} style={{ padding: '4px 10px' }}>AND (Must have ALL)</button>
                    <button type="button" className={`av-type-btn ${!tagsMatchAll ? 'active' : ''}`} onClick={() => setTagsMatchAll(false)} style={{ padding: '4px 10px' }}>OR (Match ANY)</button>
                  </div>
                )}
              </div>

              <div className="av-form-group">
                <label>Minimum Word Count (Optional)</label>
                <input 
                  type="number" 
                  min="0"
                  className="av-form-control" 
                  value={minWordCount} 
                  onChange={(e) => setMinWordCount(e.target.value)} 
                  placeholder="e.g. 500" 
                />
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '20px 0' }} />
              
              <h3 style={{ margin: '0 0 10px', fontSize: '1.1rem' }}>Article Voting Settings</h3>
              
              <div className="av-form-row">
                <div className="av-form-group">
                  <label>Wait Time (minutes)</label>
                  <input type="number" min="0" max="10080" className="av-form-control" value={articleWaitTime} onChange={(e) => setArticleWaitTime(e.target.value)} />
                </div>
                <div className="av-form-group">
                  <label>Vote Weight (%)</label>
                  <input type="number" min="1" max="100" className="av-form-control" value={articleWeight} onChange={(e) => setArticleWeight(e.target.value)} />
                </div>
                <div className="av-form-group">
                  <label>Daily Limit (Optional)</label>
                  <input type="number" min="1" className="av-form-control" value={articleDailyLimit} onChange={(e) => setArticleDailyLimit(e.target.value)} placeholder="No limit" />
                </div>
              </div>

              <div className="av-form-actions">
                <button type="submit" className="av-btn av-btn-primary" disabled={savingRule}>
                  {savingRule ? 'Saving...' : 'Save Rule'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* ACTIVE ADVANCED RULES LIST */}
      <div className="av-section" style={{ marginTop: '32px' }}>
        <h2 style={{ margin: 0, paddingBottom: '16px', borderBottom: 'none' }}>Active Advanced Rules</h2>
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading rules...</div>
          ) : rules.length === 0 ? (
            <div className="av-empty-state">
              You haven't set up any advanced autovote rules yet.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="av-rules-table">
                <thead>
                  <tr>
                    <th>Conditions</th>
                    <th>Flux Options</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(rule => {
                    let conditionText = [];
                    if (rule.target_users && rule.target_users.length) conditionText.push(`Users: ${rule.target_users.join(', ')}`);
                    if (rule.target_community_titles && rule.target_community_titles.length) {
                    conditionText.push(`Comms: ${rule.target_community_titles.join(', ')}`);
                  } else if (rule.target_communities && rule.target_communities.length) {
                    conditionText.push(`Comms: ${rule.target_communities.join(', ')}`);
                  }
                    if (rule.target_tags && rule.target_tags.length) conditionText.push(`Tags (${rule.tags_match_all ? 'ALL' : 'ANY'}): ${rule.target_tags.join(', ')}`);
                    if (rule.min_word_count) conditionText.push(`Words >= ${rule.min_word_count}`);
                    
                    return (
                      <tr key={rule.id}>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {conditionText.map((text, i) => (
                              <span key={i} style={{ fontSize: '0.85rem', background: 'var(--bg-primary)', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-color)', width: 'fit-content' }}>
                                {text}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            Wait: {rule.article_wait_time}m | Weight: {rule.article_weight}%<br/>
                            Limit: {rule.article_daily_limit || 'None'}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                            <button onClick={() => handleToggleRulePause(rule)} className="av-btn" style={{ padding: '6px 12px', fontSize: '0.85rem', backgroundColor: rule.is_paused ? 'var(--success-color, #28a745)' : 'var(--warning-color, #ffc107)', color: '#fff', border: 'none' }}>
                              {rule.is_paused ? 'Resume' : 'Pause'}
                            </button>
                            <button onClick={() => handleDeleteRule(rule.id)} className="av-btn av-btn-danger" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AutovotingAdvanced;
